import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Clamps text to a few lines by default, with a full-width bar below it
 * to expand and read the rest. Built for the tool-page sidebar's
 * "What this tool does" and "Pro tips" panels, which got noticeably
 * longer once real, substantive content replaced the earlier one-line
 * summaries -- a long block of text in a ~320px sidebar column reads as
 * a wall of text, especially once the sidebar stacks above the fold on
 * mobile. Collapsing to a preview with an explicit expand action keeps
 * the page compact without removing any of the actual content.
 *
 * Uses CSS line-clamp for the collapsed state rather than measuring
 * text height in JS -- simpler, and avoids a layout-measurement flash
 * on first render.
 */
export function CollapsibleText({
  children,
  lines = 4,
  className = "",
}: {
  children: string;
  lines?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  // Tailwind's compiler only generates CSS for class names it can find
  // as literal strings during its scan -- a dynamic template like
  // `line-clamp-${lines}` wouldn't be detected, so the clamp utility
  // simply wouldn't exist in the built CSS. Listing all supported
  // values explicitly here keeps them visible to that static scan.
  const clampClass = lines === 1 ? "line-clamp-1" : lines === 2 ? "line-clamp-2" : lines === 3 ? "line-clamp-3" : "line-clamp-4";

  // Rough, deliberately approximate check for whether this text is even
  // long enough to need collapsing -- a real measurement (comparing
  // scrollHeight to clientHeight after render) would need a ref and a
  // layout pass, adding complexity and a possible flash on first
  // render for something that just needs to be roughly right. ~90
  // characters per line is a reasonable estimate for this sidebar's
  // width and font size.
  const charsPerLine = 90;
  const likelyFitsWithoutClamping = children.length <= lines * charsPerLine;

  if (likelyFitsWithoutClamping) {
    return (
      <p className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-line ${className}`}>
        {children}
      </p>
    );
  }

  return (
    <div>
      <p
        className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-line ${className} ${
          expanded ? "" : clampClass
        }`}
      >
        {children}
      </p>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-center gap-1 mt-2 py-1 text-xs font-medium text-primary hover:text-primary/80 border-t border-border/50 transition-colors"
      >
        {expanded ? (
          <>
            Show less <ChevronUp className="h-3 w-3" />
          </>
        ) : (
          <>
            Read more <ChevronDown className="h-3 w-3" />
          </>
        )}
      </button>
    </div>
  );
}
